import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { Button } from './ui';
import { IconSend } from './icons';

/**
 * Chasing a request that has stalled. Rate limited server-side to one every six
 * hours per request, so the button reflects that rather than letting somebody
 * press it ten times and wonder why nothing happened.
 */
export function NudgeButton({ requestId, awaiting }: { requestId: string; awaiting: 'approval' | 'IT' }) {
  const queryClient = useQueryClient();

  const state = useQuery({ queryKey: ['nudge', requestId], queryFn: () => api.nudgeState(requestId) });

  const send = useMutation({
    mutationFn: () => api.nudge(requestId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['nudge', requestId] }),
  });

  if (state.isPending) return null;

  const blocked = state.data?.canNudge === false;
  const nextAt = state.data?.nextAllowedAt ? new Date(state.data.nextAllowedAt) : null;

  if (send.isSuccess) {
    return (
      <span className="text-sm text-emerald-700 dark:text-emerald-400">
        {send.data.targetRole === 'ADMIN'
          ? `Reminder sent to ${send.data.recipientCount} ${send.data.recipientCount === 1 ? 'person' : 'people'} in IT.`
          : `Reminder sent to ${send.data.recipientCount} approver${send.data.recipientCount === 1 ? '' : 's'}.`}
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        onClick={() => send.mutate()}
        disabled={blocked || send.isPending}
        title={
          blocked && nextAt
            ? `Already chased recently. You can send another at ${nextAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`
            : undefined
        }
      >
        <IconSend className="h-4 w-4" />
        {send.isPending ? 'Sending…' : 'Send reminder'}
      </Button>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {blocked && nextAt
          ? `next reminder available ${nextAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : awaiting === 'approval'
            ? 'nudges the approvers'
            : 'nudges IT'}
      </span>
      {send.isError && (
        <span className="text-xs text-copper-700 dark:text-copper-400">
          {send.error instanceof ApiError ? send.error.message : 'Could not send that reminder.'}
        </span>
      )}
    </div>
  );
}
